import { actionProps } from "@/lib/controlAnalytics";
import { useId, useState } from "react";
import { buttonClass } from "./controls";
import { PAY_CURRENCY, payAmount } from "@/lib/pay";

const BAD_AMOUNT = "Enter a whole dollar amount of up to nine digits, or leave it blank.";

type Bound = "minimum" | "maximum";

/**
 * Yearly salary bounds, editable as a draft: only Apply commits them, so a
 * person who walks away has changed nothing. Either end may be left open. A
 * refused amount stays in its field, with the fault beside it. Reset takes the
 * range off and keeps postings that state no salary.
 */
export default function PayRange({
    minimum,
    maximum,
    keepUnstated,
    onApply,
}: {
    minimum: number | null;
    maximum: number | null;
    keepUnstated: boolean;
    onApply: (minimum: number | null, maximum: number | null, keepUnstated: boolean) => void;
}) {
    const id = useId();
    const [from, setFrom] = useState(minimum?.toLocaleString("en-US") ?? "");
    const [to, setTo] = useState(maximum?.toLocaleString("en-US") ?? "");
    const [keep, setKeep] = useState(keepUnstated);
    const [fault, setFault] = useState<{ bound: Bound; message: string } | null>(null);
    function apply() {
        const lower = payAmount(from);
        const upper = payAmount(to);
        if (from.trim() && lower === null) {
            setFault({ bound: "minimum", message: BAD_AMOUNT });
        } else if (to.trim() && upper === null) {
            setFault({ bound: "maximum", message: BAD_AMOUNT });
        } else if (lower !== null && upper !== null && lower > upper) {
            setFault({ bound: "maximum", message: "Maximum salary must be at least the minimum." });
        } else {
            setFault(null);
            onApply(lower, upper, keep);
        }
    }
    const errorId = `${id}-error`;
    return (
        <form
            {...actionProps("salary_filter_submitted", { source: "PayRange" })}
            aria-label="Salary range"
            noValidate
            className="flex flex-col gap-3.5 p-4 text-[12.5px] text-fg-muted"
            onSubmit={(event) => {
                event.preventDefault();
                apply();
            }}
        >
            <div className="flex items-baseline justify-between gap-3">
                <h2 className="m-0 text-[15px] font-semibold text-fg">Salary</h2>
                <span className="whitespace-nowrap">{PAY_CURRENCY} per year</span>
            </div>
            <div className="grid grid-cols-2 gap-3 max-[360px]:grid-cols-1">
                {(
                    [
                        ["minimum", "Minimum", from, setFrom],
                        ["maximum", "Maximum", to, setTo],
                    ] as const
                ).map(([bound, label, value, setValue]) => {
                    const faulty = fault?.bound === bound;
                    return (
                        <label key={bound} className="flex min-w-0 flex-col gap-1.5">
                            {label}
                            <span
                                className={`flex h-10 min-w-0 items-center gap-1 rounded-control border bg-card px-3 text-[13px] text-fg focus-within:ring-focus max-[760px]:min-h-11 ${
                                    faulty ? "border-negative" : "border-line-control"
                                }`}
                            >
                                <span aria-hidden="true">$</span>
                                <input
                                    {...actionProps("salary_bound_edited", {
                                        source: "PayRange",
                                        field: bound,
                                    })}
                                    type="text"
                                    aria-label={`${label} salary`}
                                    inputMode="numeric"
                                    value={value}
                                    placeholder="Any"
                                    aria-invalid={faulty}
                                    aria-describedby={faulty ? errorId : undefined}
                                    onChange={(event) => {
                                        setValue(event.target.value);
                                        setFault(null);
                                    }}
                                    className="w-full min-w-0 bg-transparent font-mono outline-none"
                                />
                            </span>
                        </label>
                    );
                })}
            </div>
            {fault && (
                <p id={errorId} role="alert" className="m-0 text-negative">
                    {fault.message}
                </p>
            )}
            <label className="flex cursor-pointer items-center gap-2 text-[13px] text-fg max-[760px]:min-h-11">
                <input
                    {...actionProps("salary_unstated_toggled", { source: "PayRange" })}
                    type="checkbox"
                    checked={keep}
                    onChange={(event) => setKeep(event.target.checked)}
                    className="size-4 shrink-0 accent-brand-ink"
                />
                Include jobs without salary
            </label>
            <div className="flex items-center justify-between gap-3 border-t border-line-soft pt-3.5">
                <button
                    {...actionProps("salary_filter_cleared", { source: "PayRange" })}
                    type="button"
                    className="text-[13px] font-medium text-brand-ink underline underline-offset-2 max-[760px]:min-h-11"
                    onClick={() => onApply(null, null, true)}
                >
                    Reset
                </button>
                <button
                    {...actionProps("salary_apply_clicked", { source: "PayRange" })}
                    type="submit"
                    className={buttonClass("solid", "md", "focus-visible:ring-focus")}
                >
                    Apply
                </button>
            </div>
        </form>
    );
}
